import type {
  CalendarHeaderRead,
  CalendarHeaderCreate,
  CalendarHeaderUpdate,
  CalendarDetailResponse,
  CalendarEventCreate,
  CalendarEventRead,
  CalendarEventUpdate,
  CalendarMilestoneCreate,
  CalendarMilestoneRead,
  CalendarMilestoneUpdate,
  CalendarGridResponse,
  MilestoneRuleRead,
  PaginatedCalendarsResponse,
  ReorderRequest,
  CalendarOwner,
} from '@/types'
import type { ListCalendarsParams } from '@/api/calendarsReal'

// ─── Helpers ──────────────────────────────────────────────────────

let counter = 100
function uuid(): string {
  counter++
  return `mock-${counter}-${Math.random().toString(36).slice(2, 9)}`
}

function now(): string {
  return new Date().toISOString()
}

async function delay(ms = 200): Promise<void> {
  await new Promise(r => setTimeout(r, ms))
}

// ─── Seed Data ────────────────────────────────────────────────────

const ownerJane: CalendarOwner = {
  id: 'user-jane',
  email: 'jane.smith@michaels.com',
  display_name: 'Jane Smith',
}

const ownerMike: CalendarOwner = {
  id: 'user-mike',
  email: 'mike.johnson@michaels.com',
  display_name: 'Mike Johnson',
}

const ownerSarah: CalendarOwner = {
  id: 'user-sarah',
  email: 'sarah.lee@michaels.com',
  display_name: 'Sarah Lee',
}

// Milestones for "Product Development" event
const pdMilestones: CalendarMilestoneRead[] = [
  {
    id: 'ms-pd-1',
    event_id: 'evt-pd',
    name: 'Concept Kickoff',
    description: 'Initial concept meeting and kickoff',
    owner: ownerJane,
    milestone_type: 'FREEFORM',
    display_order: 1,
    date_mode: 'SPECIFIC',
    specific_date: '2026-02-03',
    fiscal_year: null,
    fiscal_week: null,
    calc_direction: null,
    calc_quantity: null,
    calc_unit: null,
    calc_from_milestone_id: null,
    rule_code: null,
    rule_configuration: null,
    resolved_date: '2026-02-03',
    created_at: '2026-01-10T10:00:00Z',
    updated_at: '2026-01-10T10:00:00Z',
  },
  {
    id: 'ms-pd-2',
    event_id: 'evt-pd',
    name: 'Trend Review',
    description: 'Review market trends and consumer insights',
    owner: ownerJane,
    milestone_type: 'FREEFORM',
    display_order: 2,
    date_mode: 'CALCULATED',
    specific_date: null,
    fiscal_year: null,
    fiscal_week: null,
    calc_direction: 'ADD',
    calc_quantity: 2,
    calc_unit: 'WEEK',
    calc_from_milestone_id: 'ms-pd-1',
    rule_code: null,
    rule_configuration: null,
    resolved_date: '2026-02-17',
    created_at: '2026-01-10T10:00:00Z',
    updated_at: '2026-01-10T10:00:00Z',
  },
  {
    id: 'ms-pd-3',
    event_id: 'evt-pd',
    name: 'Line List Finalized',
    description: 'All line items confirmed and finalized',
    owner: ownerJane,
    milestone_type: 'FREEFORM',
    display_order: 3,
    date_mode: 'CALCULATED',
    specific_date: null,
    fiscal_year: null,
    fiscal_week: null,
    calc_direction: 'ADD',
    calc_quantity: 2,
    calc_unit: 'WEEK',
    calc_from_milestone_id: 'ms-pd-2',
    rule_code: null,
    rule_configuration: null,
    resolved_date: '2026-03-03',
    created_at: '2026-01-10T10:00:00Z',
    updated_at: '2026-01-10T10:00:00Z',
  },
  {
    id: 'ms-pd-4',
    event_id: 'evt-pd',
    name: 'Sample Review',
    description: 'Review product samples from vendors',
    owner: ownerMike,
    milestone_type: 'FREEFORM',
    display_order: 4,
    date_mode: 'CALCULATED',
    specific_date: null,
    fiscal_year: null,
    fiscal_week: null,
    calc_direction: 'ADD',
    calc_quantity: 3,
    calc_unit: 'WEEK',
    calc_from_milestone_id: 'ms-pd-3',
    rule_code: null,
    rule_configuration: null,
    resolved_date: '2026-03-24',
    created_at: '2026-01-10T10:00:00Z',
    updated_at: '2026-01-10T10:00:00Z',
  },
  {
    id: 'ms-pd-5',
    event_id: 'evt-pd',
    name: 'Final Approval',
    description: 'Final product development approval and sign-off',
    owner: ownerJane,
    milestone_type: 'STANDARDIZED',
    display_order: 5,
    date_mode: 'CALCULATED',
    specific_date: null,
    fiscal_year: null,
    fiscal_week: null,
    calc_direction: 'ADD',
    calc_quantity: 2,
    calc_unit: 'WEEK',
    calc_from_milestone_id: 'ms-pd-4',
    rule_code: 'po_submitted',
    rule_configuration: { threshold_pct: 1.0 },
    resolved_date: '2026-04-07',
    created_at: '2026-01-10T10:00:00Z',
    updated_at: '2026-01-10T10:00:00Z',
  },
]

// Milestones for "Sourcing & Vendor Selection" event
const sourcingMilestones: CalendarMilestoneRead[] = [
  {
    id: 'ms-sv-1',
    event_id: 'evt-sv',
    name: 'Vendor RFP Sent',
    description: 'Request for proposal sent to vendors',
    owner: ownerMike,
    milestone_type: 'FREEFORM',
    display_order: 1,
    date_mode: 'SPECIFIC',
    specific_date: '2026-03-10',
    fiscal_year: null,
    fiscal_week: null,
    calc_direction: null,
    calc_quantity: null,
    calc_unit: null,
    calc_from_milestone_id: null,
    rule_code: null,
    rule_configuration: null,
    resolved_date: '2026-03-10',
    created_at: '2026-01-10T10:00:00Z',
    updated_at: '2026-01-10T10:00:00Z',
  },
  {
    id: 'ms-sv-2',
    event_id: 'evt-sv',
    name: 'Vendor Selection',
    description: 'Final vendor selection and contracts',
    owner: ownerMike,
    milestone_type: 'FREEFORM',
    display_order: 2,
    date_mode: 'CALCULATED',
    specific_date: null,
    fiscal_year: null,
    fiscal_week: null,
    calc_direction: 'ADD',
    calc_quantity: 3,
    calc_unit: 'WEEK',
    calc_from_milestone_id: 'ms-sv-1',
    rule_code: null,
    rule_configuration: null,
    resolved_date: '2026-03-31',
    created_at: '2026-01-10T10:00:00Z',
    updated_at: '2026-01-10T10:00:00Z',
  },
  {
    id: 'ms-sv-3',
    event_id: 'evt-sv',
    name: 'PO Submitted',
    description: 'Purchase orders submitted to selected vendors',
    owner: ownerMike,
    milestone_type: 'STANDARDIZED',
    display_order: 3,
    date_mode: 'SPECIFIC',
    specific_date: '2026-04-20',
    fiscal_year: null,
    fiscal_week: null,
    calc_direction: null,
    calc_quantity: null,
    calc_unit: null,
    calc_from_milestone_id: null,
    rule_code: 'po_submitted',
    rule_configuration: { threshold_pct: 1.0 },
    resolved_date: '2026-04-20',
    created_at: '2026-01-10T10:00:00Z',
    updated_at: '2026-01-10T10:00:00Z',
  },
]

// Milestones for "Supply Chain Flow" event
const scMilestones: CalendarMilestoneRead[] = [
  {
    id: 'ms-sc-1',
    event_id: 'evt-sc',
    name: 'PO Submit Deadline',
    description: 'All purchase orders must be submitted',
    owner: ownerSarah,
    milestone_type: 'FREEFORM',
    display_order: 1,
    date_mode: 'SPECIFIC',
    specific_date: null,
    fiscal_year: 2026,
    fiscal_week: 18,
    calc_direction: null,
    calc_quantity: null,
    calc_unit: null,
    calc_from_milestone_id: null,
    rule_code: null,
    rule_configuration: null,
    resolved_date: '2026-05-01',
    created_at: '2026-01-10T10:00:00Z',
    updated_at: '2026-01-10T10:00:00Z',
  },
  {
    id: 'ms-sc-2',
    event_id: 'evt-sc',
    name: 'DC Receipt',
    description: 'Items received at distribution center',
    owner: ownerSarah,
    milestone_type: 'STANDARDIZED',
    display_order: 2,
    date_mode: 'CALCULATED',
    specific_date: null,
    fiscal_year: null,
    fiscal_week: null,
    calc_direction: 'ADD',
    calc_quantity: 1,
    calc_unit: 'MONTH',
    calc_from_milestone_id: 'ms-sc-1',
    rule_code: 'items_in_dc',
    rule_configuration: { threshold_pct: 0.95, dc_id: null },
    resolved_date: '2026-06-01',
    created_at: '2026-01-10T10:00:00Z',
    updated_at: '2026-01-10T10:00:00Z',
  },
  {
    id: 'ms-sc-3',
    event_id: 'evt-sc',
    name: 'QC Complete',
    description: 'Quality control checks completed',
    owner: ownerSarah,
    milestone_type: 'FREEFORM',
    display_order: 3,
    date_mode: 'CALCULATED',
    specific_date: null,
    fiscal_year: null,
    fiscal_week: null,
    calc_direction: 'ADD',
    calc_quantity: 2,
    calc_unit: 'WEEK',
    calc_from_milestone_id: 'ms-sc-2',
    rule_code: null,
    rule_configuration: null,
    resolved_date: '2026-06-15',
    created_at: '2026-01-10T10:00:00Z',
    updated_at: '2026-01-10T10:00:00Z',
  },
]

// Events
const seedEvents: CalendarEventRead[] = [
  {
    id: 'evt-pd',
    calendar_id: 'cal-spring-2026',
    name: 'Product Development',
    description: 'Product development lifecycle from concept to approval',
    owner: ownerJane,
    display_order: 1,
    milestones: pdMilestones,
    created_at: '2026-01-10T10:00:00Z',
    updated_at: '2026-03-20T15:30:00Z',
  },
  {
    id: 'evt-sv',
    calendar_id: 'cal-spring-2026',
    name: 'Sourcing & Vendor Selection',
    description: 'Vendor sourcing, RFP, and purchase order management',
    owner: ownerMike,
    display_order: 2,
    milestones: sourcingMilestones,
    created_at: '2026-01-10T10:00:00Z',
    updated_at: '2026-03-15T12:00:00Z',
  },
  {
    id: 'evt-sc',
    calendar_id: 'cal-spring-2026',
    name: 'Supply Chain Flow',
    description: 'Supply chain logistics from PO to store delivery',
    owner: ownerSarah,
    display_order: 3,
    milestones: scMilestones,
    created_at: '2026-01-10T10:00:00Z',
    updated_at: '2026-03-10T09:00:00Z',
  },
]

// Calendars
const seedCalendars: (CalendarHeaderRead & { events: CalendarEventRead[] })[] =
  [
    {
      id: 'cal-spring-2026',
      name: 'Spring 2026 Assortment Calendar',
      description: 'Seasonal assortment planning calendar for Spring 2026',
      owner: ownerJane,
      is_active: true,
      event_count: 3,
      milestone_count: 11,
      where_in_use: ['Plan #201', 'Plan #205', 'Plan #210'],
      last_edit: '2026-03-20T15:30:00Z',
      created_at: '2026-01-10T10:00:00Z',
      updated_at: '2026-03-20T15:30:00Z',
      events: seedEvents,
    },
    {
      id: 'cal-fall-2026',
      name: 'Fall 2026 Supply Chain Calendar',
      description: 'Supply chain flow calendar for Fall 2026 season',
      owner: ownerMike,
      is_active: true,
      event_count: 4,
      milestone_count: 18,
      where_in_use: ['Plan #220'],
      last_edit: '2026-03-15T12:00:00Z',
      created_at: '2026-01-15T08:00:00Z',
      updated_at: '2026-03-15T12:00:00Z',
      events: [],
    },
    {
      id: 'cal-holiday-2026',
      name: 'Holiday 2026 Product Development',
      description:
        'Product development calendar for the Holiday 2026 collection',
      owner: ownerJane,
      is_active: false,
      event_count: 6,
      milestone_count: 30,
      where_in_use: [],
      last_edit: '2026-03-10T09:00:00Z',
      created_at: '2026-02-01T10:00:00Z',
      updated_at: '2026-03-10T09:00:00Z',
      events: [],
    },
  ]

// ─── In-memory store ──────────────────────────────────────────────

type CalendarStore = CalendarHeaderRead & { events: CalendarEventRead[] }

const calendars: CalendarStore[] = JSON.parse(JSON.stringify(seedCalendars))

function getStore(id: string): CalendarStore | undefined {
  return calendars.find(c => c.id === id)
}

function toHeader(cal: CalendarStore): CalendarHeaderRead {
  const {
    id,
    name,
    description,
    owner,
    is_active,
    event_count,
    milestone_count,
    where_in_use,
    last_edit,
    created_at,
    updated_at,
  } = cal
  return {
    id,
    name,
    description,
    owner,
    is_active,
    event_count,
    milestone_count,
    where_in_use,
    last_edit,
    created_at,
    updated_at,
  }
}

function recomputeCounts(cal: CalendarStore) {
  cal.event_count = cal.events.length
  cal.milestone_count = cal.events.reduce(
    (sum, e) => sum + e.milestones.length,
    0
  )
}

// ─── Calendar CRUD ────────────────────────────────────────────────

export async function listCalendars(
  params: ListCalendarsParams = {}
): Promise<PaginatedCalendarsResponse> {
  await delay()
  const { search, is_active, skip = 0, limit = 20 } = params

  let filtered = [...calendars]
  if (search) {
    const q = search.toLowerCase()
    filtered = filtered.filter(c => c.name.toLowerCase().includes(q))
  }
  if (is_active !== undefined) {
    filtered = filtered.filter(c => c.is_active === is_active)
  }

  const total = filtered.length
  const data = filtered.slice(skip, skip + limit)
  // Strip events from list response
  const headers: CalendarHeaderRead[] = data.map(
    ({
      id,
      name,
      description,
      owner,
      is_active,
      event_count,
      milestone_count,
      where_in_use,
      last_edit,
      created_at,
      updated_at,
    }) => ({
      id,
      name,
      description,
      owner,
      is_active,
      event_count,
      milestone_count,
      where_in_use,
      last_edit,
      created_at,
      updated_at,
    })
  )

  return {
    data: headers,
    pagination: {
      skip,
      limit,
      total,
      has_more: skip + limit < total,
    },
  }
}

export async function getCalendar(id: string): Promise<CalendarDetailResponse> {
  await delay()
  const cal = getStore(id)
  if (!cal) throw new Error(`Calendar not found: ${id}`)
  return JSON.parse(JSON.stringify(cal))
}

export async function createCalendar(
  payload: CalendarHeaderCreate
): Promise<CalendarHeaderRead> {
  await delay(300)
  const newCal: CalendarStore = {
    id: uuid(),
    name: payload.name,
    description: payload.description ?? null,
    owner: ownerJane, // default owner for mock
    is_active: true,
    event_count: 0,
    milestone_count: 0,
    where_in_use: [],
    last_edit: now(),
    created_at: now(),
    updated_at: now(),
    events: [],
  }
  calendars.unshift(newCal)
  return JSON.parse(JSON.stringify(toHeader(newCal)))
}

export async function updateCalendar(
  id: string,
  payload: CalendarHeaderUpdate
): Promise<CalendarHeaderRead> {
  await delay()
  const cal = getStore(id)
  if (!cal) throw new Error(`Calendar not found: ${id}`)

  if (payload.name !== undefined) cal.name = payload.name
  if (payload.description !== undefined) cal.description = payload.description
  if (payload.is_active !== undefined) cal.is_active = payload.is_active
  cal.updated_at = now()
  cal.last_edit = now()

  return JSON.parse(JSON.stringify(toHeader(cal)))
}

export async function deleteCalendar(id: string): Promise<void> {
  await delay()
  const idx = calendars.findIndex(c => c.id === id)
  if (idx === -1) throw new Error(`Calendar not found: ${id}`)
  if ((calendars[idx].where_in_use?.length ?? 0) > 0) {
    throw new Error('Cannot delete calendar that is in use by plans')
  }
  calendars.splice(idx, 1)
}

export async function copyCalendar(id: string): Promise<CalendarHeaderRead> {
  await delay(400)
  const cal = getStore(id)
  if (!cal) throw new Error(`Calendar not found: ${id}`)

  const copy: CalendarStore = JSON.parse(JSON.stringify(cal))
  copy.id = uuid()
  copy.name = `${cal.name} (Copy)`
  copy.where_in_use = []
  copy.created_at = now()
  copy.updated_at = now()
  copy.last_edit = now()

  // Reassign IDs for events and milestones
  const milestoneIdMap: Record<string, string> = {}
  for (const event of copy.events) {
    event.id = uuid()
    event.calendar_id = copy.id
    for (const ms of event.milestones) {
      const oldId = ms.id
      ms.id = uuid()
      ms.event_id = event.id
      milestoneIdMap[oldId] = ms.id
    }
  }
  // Fix calc_from_milestone_id references
  for (const event of copy.events) {
    for (const ms of event.milestones) {
      if (
        ms.calc_from_milestone_id &&
        milestoneIdMap[ms.calc_from_milestone_id]
      ) {
        ms.calc_from_milestone_id = milestoneIdMap[ms.calc_from_milestone_id]
      }
    }
  }

  calendars.unshift(copy)
  return JSON.parse(JSON.stringify(toHeader(copy)))
}

// ─── Event CRUD ───────────────────────────────────────────────────

export async function createCalendarEvent(
  calendarId: string,
  payload: CalendarEventCreate
): Promise<CalendarEventRead> {
  await delay()
  const cal = getStore(calendarId)
  if (!cal) throw new Error(`Calendar not found: ${calendarId}`)

  const newEvent: CalendarEventRead = {
    id: uuid(),
    calendar_id: calendarId,
    name: payload.name,
    description: payload.description ?? null,
    owner: payload.owner_id ? ownerJane : null,
    display_order: cal.events.length + 1,
    milestones: [],
    created_at: now(),
    updated_at: now(),
  }
  cal.events.push(newEvent)
  recomputeCounts(cal)
  cal.updated_at = now()
  cal.last_edit = now()
  return JSON.parse(JSON.stringify(newEvent))
}

export async function updateCalendarEvent(
  calendarId: string,
  eventId: string,
  payload: CalendarEventUpdate
): Promise<CalendarEventRead> {
  await delay()
  const cal = getStore(calendarId)
  if (!cal) throw new Error(`Calendar not found: ${calendarId}`)
  const event = cal.events.find(e => e.id === eventId)
  if (!event) throw new Error(`Event not found: ${eventId}`)

  if (payload.name !== undefined) event.name = payload.name
  if (payload.description !== undefined) event.description = payload.description
  event.updated_at = now()
  cal.updated_at = now()
  cal.last_edit = now()

  return JSON.parse(JSON.stringify(event))
}

export async function deleteCalendarEvent(
  calendarId: string,
  eventId: string
): Promise<void> {
  await delay()
  const cal = getStore(calendarId)
  if (!cal) throw new Error(`Calendar not found: ${calendarId}`)
  const idx = cal.events.findIndex(e => e.id === eventId)
  if (idx === -1) throw new Error(`Event not found: ${eventId}`)
  cal.events.splice(idx, 1)
  // Re-assign display_order
  cal.events.forEach((e, i) => {
    e.display_order = i + 1
  })
  recomputeCounts(cal)
  cal.updated_at = now()
  cal.last_edit = now()
}

export async function reorderCalendarEvents(
  calendarId: string,
  payload: ReorderRequest
): Promise<void> {
  await delay()
  const cal = getStore(calendarId)
  if (!cal) throw new Error(`Calendar not found: ${calendarId}`)

  const reordered: CalendarEventRead[] = []
  for (const id of payload.ordered_ids) {
    const event = cal.events.find(e => e.id === id)
    if (event) reordered.push(event)
  }
  reordered.forEach((e, i) => {
    e.display_order = i + 1
  })
  cal.events = reordered
  cal.updated_at = now()
  cal.last_edit = now()
}

// ─── Milestone CRUD ───────────────────────────────────────────────

export async function createMilestone(
  calendarId: string,
  eventId: string,
  payload: CalendarMilestoneCreate
): Promise<CalendarMilestoneRead> {
  await delay()
  const cal = getStore(calendarId)
  if (!cal) throw new Error(`Calendar not found: ${calendarId}`)
  const event = cal.events.find(e => e.id === eventId)
  if (!event) throw new Error(`Event not found: ${eventId}`)

  const newMs: CalendarMilestoneRead = {
    id: uuid(),
    event_id: eventId,
    name: payload.name,
    description: payload.description ?? null,
    owner: null,
    milestone_type: payload.milestone_type,
    display_order: event.milestones.length + 1,
    date_mode: payload.date_mode,
    specific_date: payload.specific_date ?? null,
    fiscal_year: payload.fiscal_year ?? null,
    fiscal_week: payload.fiscal_week ?? null,
    calc_direction: payload.calc_direction ?? null,
    calc_quantity: payload.calc_quantity ?? null,
    calc_unit: payload.calc_unit ?? null,
    calc_from_milestone_id: payload.calc_from_milestone_id ?? null,
    rule_code: payload.rule_code ?? null,
    rule_configuration: payload.rule_configuration ?? null,
    resolved_date: payload.specific_date ?? null,
    created_at: now(),
    updated_at: now(),
  }
  event.milestones.push(newMs)
  recomputeCounts(cal)
  cal.updated_at = now()
  cal.last_edit = now()
  return JSON.parse(JSON.stringify(newMs))
}

export async function updateMilestone(
  calendarId: string,
  eventId: string,
  msId: string,
  payload: CalendarMilestoneUpdate
): Promise<CalendarMilestoneRead> {
  await delay()
  const cal = getStore(calendarId)
  if (!cal) throw new Error(`Calendar not found: ${calendarId}`)
  const event = cal.events.find(e => e.id === eventId)
  if (!event) throw new Error(`Event not found: ${eventId}`)
  const ms = event.milestones.find(m => m.id === msId)
  if (!ms) throw new Error(`Milestone not found: ${msId}`)

  if (payload.name !== undefined) ms.name = payload.name
  if (payload.description !== undefined)
    ms.description = payload.description ?? null
  if (payload.milestone_type !== undefined)
    ms.milestone_type = payload.milestone_type
  if (payload.date_mode !== undefined) ms.date_mode = payload.date_mode
  if (payload.specific_date !== undefined)
    ms.specific_date = payload.specific_date ?? null
  if (payload.fiscal_year !== undefined)
    ms.fiscal_year = payload.fiscal_year ?? null
  if (payload.fiscal_week !== undefined)
    ms.fiscal_week = payload.fiscal_week ?? null
  if (payload.calc_direction !== undefined)
    ms.calc_direction = payload.calc_direction ?? null
  if (payload.calc_quantity !== undefined)
    ms.calc_quantity = payload.calc_quantity ?? null
  if (payload.calc_unit !== undefined) ms.calc_unit = payload.calc_unit ?? null
  if (payload.calc_from_milestone_id !== undefined)
    ms.calc_from_milestone_id = payload.calc_from_milestone_id ?? null
  if (payload.rule_code !== undefined) ms.rule_code = payload.rule_code ?? null
  if (payload.rule_configuration !== undefined)
    ms.rule_configuration = payload.rule_configuration ?? null

  // Recompute resolved_date for specific dates
  if (ms.date_mode === 'SPECIFIC' && ms.specific_date) {
    ms.resolved_date = ms.specific_date
  }

  ms.updated_at = now()
  cal.updated_at = now()
  cal.last_edit = now()
  return JSON.parse(JSON.stringify(ms))
}

export async function deleteMilestone(
  calendarId: string,
  eventId: string,
  msId: string
): Promise<void> {
  await delay()
  const cal = getStore(calendarId)
  if (!cal) throw new Error(`Calendar not found: ${calendarId}`)
  const event = cal.events.find(e => e.id === eventId)
  if (!event) throw new Error(`Event not found: ${eventId}`)
  const idx = event.milestones.findIndex(m => m.id === msId)
  if (idx === -1) throw new Error(`Milestone not found: ${msId}`)
  event.milestones.splice(idx, 1)
  event.milestones.forEach((m, i) => {
    m.display_order = i + 1
  })
  recomputeCounts(cal)
  cal.updated_at = now()
  cal.last_edit = now()
}

export async function reorderMilestones(
  calendarId: string,
  eventId: string,
  payload: ReorderRequest
): Promise<void> {
  await delay()
  const cal = getStore(calendarId)
  if (!cal) throw new Error(`Calendar not found: ${calendarId}`)
  const event = cal.events.find(e => e.id === eventId)
  if (!event) throw new Error(`Event not found: ${eventId}`)

  const reordered: CalendarMilestoneRead[] = []
  for (const id of payload.ordered_ids) {
    const ms = event.milestones.find(m => m.id === id)
    if (ms) reordered.push(ms)
  }
  reordered.forEach((m, i) => {
    m.display_order = i + 1
  })
  event.milestones = reordered
  cal.updated_at = now()
  cal.last_edit = now()
}

// ─── Grid View ────────────────────────────────────────────────────

export async function getCalendarGrid(
  calendarId: string
): Promise<CalendarGridResponse> {
  await delay()
  const cal = getStore(calendarId)
  if (!cal) throw new Error(`Calendar not found: ${calendarId}`)

  // Collect all unique milestone names ordered by earliest occurrence
  const nameOrder: string[] = []
  for (const event of cal.events) {
    for (const ms of event.milestones) {
      if (!nameOrder.includes(ms.name)) {
        nameOrder.push(ms.name)
      }
    }
  }

  const columns = nameOrder.map((name, idx) => ({
    name,
    column_index: idx,
  }))

  const rows = cal.events.map(event => ({
    event_id: event.id,
    event_name: event.name,
    display_order: event.display_order,
    cells: event.milestones
      .map(ms => {
        const colIdx = nameOrder.indexOf(ms.name)
        if (colIdx === -1) return null
        return {
          column_index: colIdx,
          milestone_id: ms.id,
          resolved_date: ms.resolved_date,
          date_mode: ms.date_mode,
          fiscal_week_label: ms.fiscal_week
            ? `FW${String(ms.fiscal_week).padStart(2, '0')}`
            : null,
          is_editable: ms.date_mode === 'SPECIFIC',
        }
      })
      .filter(Boolean) as CalendarGridResponse['rows'][number]['cells'],
  }))

  return { columns, rows }
}

// ─── Milestone Rules ──────────────────────────────────────────────

export async function listMilestoneRules(): Promise<MilestoneRuleRead[]> {
  await delay(100)
  return [
    {
      code: 'po_submitted',
      name: 'PO Submitted',
      description:
        'Checks whether purchase orders have been submitted for all line items in the plan.',
      config_schema: {
        threshold_pct: {
          type: 'float',
          default: 1.0,
          description:
            'Fraction of line items that must have POs submitted (1.0 = 100%)',
        },
        green_buffer_days: {
          type: 'int',
          default: 14,
          description: 'Days before target date that count as green',
        },
      },
    },
    {
      code: 'items_in_dc',
      name: 'Items in DC',
      description:
        'Checks whether items have been received at the distribution center.',
      config_schema: {
        threshold_pct: {
          type: 'float',
          default: 1.0,
          description: 'Fraction of line items received',
        },
        dc_id: {
          type: 'string',
          default: null,
          description: 'Specific DC to check, or all DCs if omitted',
        },
      },
    },
  ]
}
